// Sample property data for the design showcase.
// Images curated from Unsplash & Pexels (open editorial license).

export const COLLECTIONS = [
  { id: "coastal", label: "Coastal escapes", count: 42 },
  { id: "cabins", label: "Quiet cabins", count: 28 },
  { id: "city", label: "City lofts", count: 61 },
  { id: "desert", label: "Desert retreats", count: 17 },
  { id: "aframe", label: "A-frame hideouts", count: 12 },
  { id: "tropical", label: "Tropical bungalows", count: 33 },
];

export const AMENITY_OPTIONS = [
  "Wi-Fi",
  "Kitchen",
  "Pool",
  "Hot tub",
  "Fireplace",
  "Pets allowed",
  "Workspace",
  "Free parking",
  "Ocean view",
  "Mountain view",
];

// Shared house rules — same across properties for now, but per-property overrides
// can be added later via property.houseRules.
export const HOUSE_RULES = [
  { icon: "clock-in", label: "Check-in", value: "After 3:00 PM" },
  { icon: "clock-out", label: "Check-out", value: "Before 11:00 AM" },
  { icon: "guests", label: "Max guests", value: "Per listing capacity" },
  { icon: "quiet", label: "Quiet hours", value: "10:00 PM — 8:00 AM" },
  { icon: "smoking", label: "Smoking", value: "Not permitted indoors" },
  { icon: "parties", label: "Parties & events", value: "Not permitted" },
  { icon: "pets", label: "Pets", value: "By host approval" },
  { icon: "id", label: "Government ID", value: "Required at check-in" },
];

// Shared FAQ — inspired by the staging StayEasy property page.
export const FAQS = [
  {
    q: "How will I receive check-in instructions?",
    a: "Check-in details are shared after booking confirmation via email and the booking dashboard, usually 48 hours before arrival.",
  },
  {
    q: "Is parking available at the property?",
    a: "Most stays include on-site or nearby parking. Exact number of spots and any vehicle restrictions are noted on the listing.",
  },
  {
    q: "Do you provide toiletries and basic essentials?",
    a: "Every stay includes fresh linens, towels, and starter toiletries. Exact inclusions may vary by property.",
  },
  {
    q: "Is a security deposit required?",
    a: "Some properties require a refundable security deposit. If applicable, the amount and terms appear before you confirm your booking.",
  },
  {
    q: "Does the property have Wi-Fi?",
    a: "Yes — complimentary Wi-Fi is available for guests. Network details are shared with your check-in instructions.",
  },
  {
    q: "What time is check-in and check-out?",
    a: "Standard check-in is from 3:00 PM and check-out is by 11:00 AM. Early check-in or late check-out may be possible on request.",
  },
  {
    q: "Can I book for a long stay?",
    a: "Yes — long stays (28+ nights) are welcome at most properties, often at a reduced nightly rate. Please contact the host for details.",
  },
  {
    q: "What is the cancellation policy?",
    a: "Free cancellation up to 7 days before check-in. Between 7 days and 48 hours: 50% refund. Within 48 hours of check-in: non-refundable.",
  },
];

export const PROPERTIES = [
  {
    id: "villa-tramonto",
    name: "Villa Tramonto",
    location: "Amalfi Coast, Italy",
    collection: "coastal",
    price: 640,
    rating: 4.96,
    reviews: 218,
    guests: 8,
    bedrooms: 4,
    baths: 3,
    host: { name: "Isabella R.", years: 6, avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=300&q=80" },
    amenities: ["Wi-Fi", "Kitchen", "Pool", "Ocean view", "Free parking", "Workspace"],
    description:
      "A whitewashed villa carved into the cliffs of Amalfi, with a saltwater infinity pool that dissolves into the Tyrrhenian sea. Vaulted ceilings, hand-thrown ceramics, and slow mornings on the terrace.",
    images: [
      "https://images.unsplash.com/photo-1745761320791-5ae142edee8c?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
      "https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1505873242700-f289a29e1e0f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1668438712649-ffd85f756de5?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1710563138874-4bac91c14e51?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
  },
  {
    id: "black-ridge-cabin",
    name: "Black Ridge Cabin",
    location: "Aspen, Colorado",
    collection: "cabins",
    price: 380,
    rating: 4.89,
    reviews: 142,
    guests: 6,
    bedrooms: 3,
    baths: 2,
    host: { name: "Marcus L.", years: 4, avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&w=300&q=80" },
    amenities: ["Wi-Fi", "Kitchen", "Fireplace", "Mountain view", "Hot tub", "Pets allowed"],
    description:
      "A blackened cedar cabin balanced above a pine slope. Floor-to-ceiling glass, a wood stove that hums through winter, and a hot tub open to the sky.",
    images: [
      "https://images.unsplash.com/photo-1551648746-d158bcd704e7?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
      "https://images.unsplash.com/photo-1475087542963-13ab5e611954?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1575403071235-5dcd06cbf169?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1570793005386-840846445fed?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1505873242700-f289a29e1e0f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
  },
  {
    id: "warehouse-08",
    name: "Warehouse 08",
    location: "Brooklyn, New York",
    collection: "city",
    price: 295,
    rating: 4.82,
    reviews: 309,
    guests: 4,
    bedrooms: 2,
    baths: 2,
    host: { name: "June K.", years: 7, avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=300&q=80" },
    amenities: ["Wi-Fi", "Kitchen", "Workspace", "Free parking"],
    description:
      "A restored 1928 print warehouse in DUMBO. Exposed brick, iron columns, and a bank of arched windows that frame the Manhattan Bridge.",
    images: [
      "https://images.unsplash.com/photo-1668438712649-ffd85f756de5?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
      "https://images.unsplash.com/photo-1505873242700-f289a29e1e0f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.pexels.com/photos/4703/inside-apartment-design-home.jpg?auto=compress&cs=tinysrgb&w=1600",
      "https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1745761320791-5ae142edee8c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
  },
  {
    id: "casa-ocre",
    name: "Casa Ocre",
    location: "Joshua Tree, California",
    collection: "desert",
    price: 410,
    rating: 4.94,
    reviews: 176,
    guests: 5,
    bedrooms: 2,
    baths: 2,
    host: { name: "Amara S.", years: 3, avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=300&q=80" },
    amenities: ["Wi-Fi", "Kitchen", "Pool", "Fireplace", "Workspace"],
    description:
      "A sculptural adobe hidden between boulders. Terracotta walls, a lap pool the color of malachite, and a courtyard that catches the last of the desert light.",
    images: [
      "https://images.unsplash.com/photo-1649181353619-d3ed1e023984?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
      "https://images.unsplash.com/photo-1561065091-4908548ee638?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1575403071235-5dcd06cbf169?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1668438712649-ffd85f756de5?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1710563138874-4bac91c14e51?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
  },
  {
    id: "the-north-aframe",
    name: "The North A-Frame",
    location: "Whistler, Canada",
    collection: "aframe",
    price: 265,
    rating: 4.87,
    reviews: 98,
    guests: 4,
    bedrooms: 2,
    baths: 1,
    host: { name: "Elliot D.", years: 5, avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&w=300&q=80" },
    amenities: ["Wi-Fi", "Kitchen", "Fireplace", "Mountain view", "Pets allowed"],
    description:
      "A cedar A-frame tucked in a cathedral of Douglas firs. Skylights, a reading loft, and a wood-burning sauna at the edge of the trees.",
    images: [
      "https://images.unsplash.com/photo-1575403071235-5dcd06cbf169?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
      "https://images.unsplash.com/photo-1570793005386-840846445fed?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1551648746-d158bcd704e7?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1475087542963-13ab5e611954?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1505873242700-f289a29e1e0f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
  },
  {
    id: "palm-bungalow",
    name: "Palm Bungalow 04",
    location: "Nusa Lembongan, Bali",
    collection: "tropical",
    price: 220,
    rating: 4.91,
    reviews: 262,
    guests: 3,
    bedrooms: 1,
    baths: 1,
    host: { name: "Wayan P.", years: 8, avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=300&q=80" },
    amenities: ["Wi-Fi", "Pool", "Ocean view", "Kitchen"],
    description:
      "An open-air bamboo bungalow steps from a quiet reef. Outdoor rain shower, a private plunge pool, and mornings that begin with the sound of the surf.",
    images: [
      "https://images.unsplash.com/photo-1710563138874-4bac91c14e51?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
      "https://images.unsplash.com/photo-1645378975326-7339786fdec3?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1745761320791-5ae142edee8c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1668438712649-ffd85f756de5?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
  },
  {
    id: "casa-lina",
    name: "Casa Lina",
    location: "Tulum, Mexico",
    collection: "tropical",
    price: 340,
    rating: 4.88,
    reviews: 149,
    guests: 6,
    bedrooms: 3,
    baths: 2,
    host: { name: "Sofía M.", years: 5, avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=300&q=80" },
    amenities: ["Wi-Fi", "Kitchen", "Pool", "Ocean view", "Free parking"],
    description:
      "A Yucatecan retreat wrapped in tropical hardwoods and lime plaster. A cenote-style pool sits in the courtyard beneath a canopy of chit palms.",
    images: [
      "https://images.unsplash.com/photo-1645378975326-7339786fdec3?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
      "https://images.unsplash.com/photo-1710563138874-4bac91c14e51?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1745761320791-5ae142edee8c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1668438712649-ffd85f756de5?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
  },
  {
    id: "hus-nord",
    name: "Hus Nord",
    location: "Lofoten, Norway",
    collection: "cabins",
    price: 315,
    rating: 4.93,
    reviews: 87,
    guests: 4,
    bedrooms: 2,
    baths: 1,
    host: { name: "Ingrid H.", years: 6, avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&w=300&q=80" },
    amenities: ["Wi-Fi", "Kitchen", "Fireplace", "Ocean view", "Workspace"],
    description:
      "A traditional red hytte on stilts above the fjord. Perfect for chasing the northern lights, then thawing by the wood stove with something warm.",
    images: [
      "https://images.unsplash.com/photo-1475087542963-13ab5e611954?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
      "https://images.unsplash.com/photo-1551648746-d158bcd704e7?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1575403071235-5dcd06cbf169?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1570793005386-840846445fed?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1505873242700-f289a29e1e0f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
  },
  {
    id: "the-white-loft",
    name: "The White Loft",
    location: "Lisbon, Portugal",
    collection: "city",
    price: 210,
    rating: 4.79,
    reviews: 421,
    guests: 3,
    bedrooms: 1,
    baths: 1,
    host: { name: "Rui A.", years: 4, avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=300&q=80" },
    amenities: ["Wi-Fi", "Kitchen", "Workspace"],
    description:
      "A minimal white-plaster loft in Príncipe Real. Terrazzo floors, a tiny balcony over a jacaranda-lined street, and coffee shops one block away.",
    images: [
      "https://images.unsplash.com/photo-1505873242700-f289a29e1e0f?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
      "https://images.unsplash.com/photo-1668438712649-ffd85f756de5?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.pexels.com/photos/4703/inside-apartment-design-home.jpg?auto=compress&cs=tinysrgb&w=1600",
      "https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
      "https://images.unsplash.com/photo-1745761320791-5ae142edee8c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    ],
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "It felt less like a booking and more like being handed the key to someone's favorite secret. Every room, every detail — considered.",
    author: "Léa Morel",
    role: "Photographer, Paris",
  },
  {
    quote:
      "We've stayed in fifty places this year. Two we'd go back to. Both were on StayEasy.",
    author: "Ben & Naya",
    role: "Travelling writers",
  },
  {
    quote:
      "The kind of places you don't post about, because you'd rather keep them.",
    author: "Kenji Ito",
    role: "Chef, Osaka",
  },
];
